import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api from '../services/api';
import {
    Box,
    Typography,
    Container,
    Paper,
    Card,
    CardContent,
    Grid,
    CircularProgress,
    Fade,
    Grow,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Divider,
    Alert,
    TextField,
    IconButton,
    InputAdornment,
    Chip,
    Collapse,
    ListSubheader,
    Checkbox
} from '@mui/material';
import {
    TrendingUp,
    TrendingDown,
    AccountBalanceWallet,
    Receipt,
    ReceiptLong,
    Download,
    Gavel,
    Calculate,
    CalendarMonth,
    PersonAdd,
    Delete,
    Person,
    LocationOn,
    ChildCare,
    ExpandMore,
    ExpandLess,
    Save,
    CloudUpload
} from '@mui/icons-material';
import { TAX_CATEGORIES, TAX_CAT_MAP, TAX_GROUPS, TAX_CAT_LABELS } from '../constants/taxCategories';
import { PERSONAL_CAT_LABELS } from '../constants/personalCategories';
import TransactionSearch from './TransactionSearch';

const FILING_STATUS_LABELS = {
    single: 'Single',
    mfj: 'Married Filing Jointly',
    mfs: 'Married Filing Separately',
    hoh: 'Head of Household'
};

const FEDERAL_BRACKETS = {
    single:  { std: 15000, rates: [[11925, 0.10], [48475, 0.12], [103350, 0.22], [197300, 0.24], [250525, 0.32], [626350, 0.35], [Infinity, 0.37]] },
    mfj:     { std: 30000, rates: [[23850, 0.10], [96950, 0.12], [206700, 0.22], [394600, 0.24], [501050, 0.32], [751600, 0.35], [Infinity, 0.37]] },
    mfs:     { std: 15000, rates: [[11925, 0.10], [48475, 0.12], [103350, 0.22], [197300, 0.24], [250525, 0.32], [375800, 0.35], [Infinity, 0.37]] },
    hoh:     { std: 22500, rates: [[17000, 0.10], [64850, 0.12], [103350, 0.22], [197300, 0.24], [250500, 0.32], [626350, 0.35], [Infinity, 0.37]] },
};

const CA_BRACKETS = {
    single: { std: 5540, rates: [[10412, 0.01], [24684, 0.02], [38959, 0.04], [54081, 0.06], [68350, 0.08], [349137, 0.093], [418961, 0.103], [698271, 0.113], [Infinity, 0.123]] },
    mfj:    { std: 11080, rates: [[20824, 0.01], [49368, 0.02], [77918, 0.04], [108162, 0.06], [136700, 0.08], [698274, 0.093], [837922, 0.103], [1396542, 0.113], [Infinity, 0.123]] },
    mfs:    { std: 5540, rates: [[10412, 0.01], [24684, 0.02], [38959, 0.04], [54081, 0.06], [68350, 0.08], [349137, 0.093], [418961, 0.103], [698271, 0.113], [Infinity, 0.123]] },
    hoh:    { std: 11080, rates: [[20839, 0.01], [49371, 0.02], [63644, 0.04], [78765, 0.06], [93037, 0.08], [474750, 0.093], [569691, 0.103], [949484, 0.113], [Infinity, 0.123]] },
};

const SS_WAGE_BASE = 176100;
const CA_MENTAL_HEALTH_THRESHOLD = 1000000;

function calcBracketTax(taxableIncome, rates) {
    let tax = 0;
    let remaining = taxableIncome;
    let prev = 0;
    const details = [];
    for (const [limit, rate] of rates) {
        const width = limit - prev;
        const amt = Math.min(remaining, width);
        if (amt <= 0) break;
        const t = amt * rate;
        tax += t;
        details.push({ rate: (rate * 100).toFixed(1).replace(/\.0$/, ''), taxable: amt, tax: t });
        remaining -= amt;
        prev = limit;
    }
    return { tax, details };
}

const NON_EXPENSE_CATEGORIES = new Set(['p53', 'p57']);

function TaxSummary() {
    const currentYear = new Date().getFullYear();
    const [taxYear, setTaxYear] = useState(currentYear - 1);
    const [transactions, setTransactions] = useState([]);
    const [personalBizTx, setPersonalBizTx] = useState([]);
    const [payrollEntries, setPayrollEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetched, setFetched] = useState(false);
    const [filingStatus, setFilingStatus] = useState('mfj');
    const [w2Entries, setW2Entries] = useState([]);
    const [dependents, setDependents] = useState([
        { id: 1, name: 'Child 1', age: '11' },
        { id: 2, name: 'Child 2', age: '6' },
    ]);
    const [expandedIncome, setExpandedIncome] = useState({});
    const [expandedExpense, setExpandedExpense] = useState({});
    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null);

    useEffect(() => {
        setLoading(true);
        setFetched(false);
        const start = `${taxYear}-01-01`;
        const end = `${taxYear}-12-31`;
        const params = { startDate: start, endDate: end };

        Promise.all([
            api.get(`/api/csv/expenses`, { params }),
            api.get(`/api/personal-finance/transactions/business`, { params })
                .catch(() => ({ data: [] })),
            api.get(`/api/csv/payroll`, { params: { taxYear } })
                .catch(() => ({ data: [] }))
        ]).then(([bankRes, pfRes, payrollRes]) => {
            setTransactions(bankRes.data || []);
            setPersonalBizTx(pfRes.data || []);
            setPayrollEntries(payrollRes.data || []);
        }).catch(err => {
            console.error('Error fetching transactions:', err);
            setTransactions([]);
            setPersonalBizTx([]);
            setPayrollEntries([]);
        }).finally(() => {
            setLoading(false);
            setFetched(true);
        });
    }, [taxYear]);

    const [w2Uploading, setW2Uploading] = useState(false);

    useEffect(() => {
        api.get('/api/csv/w2', { params: { taxYear } })
            .then(res => {
                const docs = res.data || [];
                if (docs.length > 0) {
                    setW2Entries(prev => {
                        const existingLabels = new Set(prev.map(e => e.label));
                        const newEntries = docs
                            .filter(d => !existingLabels.has(`${d.employeeName || ''} - ${d.employerName || ''}`))
                            .map(d => ({
                                id: Date.now() + Math.random(),
                                label: `${d.employeeName || ''} - ${d.employerName || ''}`,
                                wages: String(d.wages || ''),
                                fedWithheld: String(d.federalWithheld || ''),
                                stateWithheld: String(d.stateIncomeTax || ''),
                                fromDb: true
                            }));
                        if (newEntries.length > 0 && prev.length === 0) return newEntries;
                        return prev;
                    });
                }
            })
            .catch(() => {});
    }, [taxYear]);

    const handleW2Upload = useCallback((e) => {
        const file = e.target.files[0];
        if (!file) return;
        setW2Uploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('taxYear', taxYear);
        api.post('/api/csv/uploadW2', formData)
            .then(res => {
                const w2 = res.data.w2;
                if (w2) {
                    const newEntry = {
                        id: Date.now(),
                        label: `${w2.employeeName || ''} - ${w2.employerName || ''}`,
                        wages: String(w2.wages || ''),
                        fedWithheld: String(w2.federalWithheld || ''),
                        stateWithheld: String(w2.stateIncomeTax || ''),
                        fromDb: true
                    };
                    setW2Entries(prev => {
                        const existing = prev.findIndex(e =>
                            e.label === newEntry.label || e.label === '');
                        if (existing >= 0) {
                            const updated = [...prev];
                            updated[existing] = newEntry;
                            return updated;
                        }
                        return [...prev, newEntry];
                    });
                }
            })
            .catch(err => {
                alert('Failed to parse W-2: ' + (err.response?.data?.error || err.message));
            })
            .finally(() => {
                setW2Uploading(false);
                e.target.value = '';
            });
    }, [taxYear]);

    const addW2 = useCallback(() => {
        setW2Entries(prev => [...prev, { id: Date.now(), label: '', wages: '', fedWithheld: '', stateWithheld: '' }]);
    }, []);

    const removeW2 = useCallback((id) => {
        setW2Entries(prev => prev.filter(e => e.id !== id));
    }, []);

    const updateW2 = useCallback((id, field, value) => {
        setW2Entries(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));
    }, []);

    const addDependent = useCallback(() => {
        setDependents(prev => [...prev, { id: Date.now(), name: '', age: '' }]);
    }, []);

    const removeDependent = useCallback((id) => {
        setDependents(prev => prev.filter(d => d.id !== id));
    }, []);

    const updateDependent = useCallback((id, field, value) => {
        setDependents(prev => prev.map(d => d.id === id ? { ...d, [field]: value } : d));
    }, []);

    const isBusinessTransaction = (tx) => {
        const type = tx.classification || tx.tag?.type || '';
        return type.toLowerCase() !== 'personal';
    };

    const payrollTotals = useMemo(() => {
        const t = { grossPay: 0, employerTaxesTotal: 0, employerBenefits: 0 };
        payrollEntries.forEach(e => {
            t.grossPay += (e.grossPay || 0);
            t.employerTaxesTotal += (e.employerTaxesTotal || 0);
            t.employerBenefits += (e.traditional401kCompany || 0) + (e.roth401kCompany || 0)
                + (e.dentalCompany || 0) + (e.visionCompany || 0) + (e.medicalCompany || 0);
        });
        t.total = t.grossPay + t.employerTaxesTotal + t.employerBenefits;
        return t;
    }, [payrollEntries]);

    const { incomeBySource, expenseByCategory, totalIncome, totalExpenses, netProfit, allIncomeTxMap, allExpenseTxMap } = useMemo(() => {
        const isBiz = (tx) => {
            const type = tx.classification || tx.tag?.type || '';
            return type.toLowerCase() !== 'personal';
        };
        const incomeMap = {};
        const expenseMap = {};
        const allIncomeTxMap = {};
        const allExpenseTxMap = {};

        const hasPayrollKeyword = (desc) => {
            const d = (desc || '').replace(/\s+/g, ' ').toLowerCase();
            const collapsed = d.replace(/\s/g, '');
            if (/payroll|paycheck|last pay\b|pay discrepancy|off-cycle|final pay/i.test(d)) return true;
            if (/payment\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(d)) return true;
            if (/payroll|paycheck|lastpay|paydiscrepancy|offcycle|finalpay/i.test(collapsed)) return true;
            return false;
        };

        const normalizeVendor = (name, description) => {
            if (!name) return 'Uncategorized';
            const upper = name.toUpperCase().replace(/[,.\s]+/g, ' ').trim();
            if (upper === 'GUSTO' || upper === 'GUSTO INC') {
                const desc = (description || '').toUpperCase();
                if (desc.includes('DES:FEE')) return 'Gusto Fees';
                return 'Gusto';
            }
            if (hasPayrollKeyword(description)) return 'Other Payroll';
            return name;
        };

        transactions.forEach(tx => {
            const vendorLabel = normalizeVendor(tx.tag?.tag, tx.description);
            const amount = Math.abs(tx.amount || 0);
            const isBusiness = isBiz(tx);
            if (!isBusiness) return;

            const taxCat = tx.taxCategory;
            if (NON_EXPENSE_CATEGORIES.has(taxCat)) return;

            const category = taxCat && TAX_CAT_LABELS[taxCat]
                ? `${taxCat.toUpperCase()} ${TAX_CAT_LABELS[taxCat]}`
                : vendorLabel;

            if (tx.type?.toLowerCase() === 'deposit') {
                incomeMap[category] = (incomeMap[category] || 0) + amount;
                if (!allIncomeTxMap[category]) allIncomeTxMap[category] = [];
                allIncomeTxMap[category].push(tx);
            } else if (tx.type?.toLowerCase() === 'expense') {
                expenseMap[category] = (expenseMap[category] || 0) + amount;
                if (!allExpenseTxMap[category]) allExpenseTxMap[category] = [];
                allExpenseTxMap[category].push(tx);
            }
        });

        personalBizTx.forEach(pfTx => {
            const rawAmt = parseFloat(pfTx.amount) || 0;
            const amt = Math.abs(rawAmt);
            if (amt === 0) return;

            const taxCat = pfTx.taxCategory;
            if (NON_EXPENSE_CATEGORIES.has(taxCat)) return;

            const label = taxCat && TAX_CAT_LABELS[taxCat]
                ? `${taxCat.toUpperCase()} ${TAX_CAT_LABELS[taxCat]}`
                : `[Personal] ${pfTx.categoryPrimary || pfTx.merchantName || pfTx.description || 'Uncategorized'}`;

            const isExpense = rawAmt < 0;
            const syntheticTx = {
                id: `pf-${pfTx.id}`,
                description: pfTx.description || pfTx.merchantName || '',
                amount: pfTx.amount,
                date: pfTx.transactionDate,
                classification: 'Business',
                taxCategory: taxCat || '',
                personalCategory: pfTx.personalCategory,
                tag: { tag: label, type: 'Business' },
                type: isExpense ? 'expense' : 'deposit',
                source: 'personal-finance'
            };

            if (isExpense) {
                expenseMap[label] = (expenseMap[label] || 0) + amt;
                if (!allExpenseTxMap[label]) allExpenseTxMap[label] = [];
                allExpenseTxMap[label].push(syntheticTx);
            } else {
                incomeMap[label] = (incomeMap[label] || 0) + amt;
                if (!allIncomeTxMap[label]) allIncomeTxMap[label] = [];
                allIncomeTxMap[label].push(syntheticTx);
            }
        });

        if (payrollTotals.total > 0) {
            const payrollItems = [
                { label: 'Payroll — Gross Wages (from Payroll Journal)', amount: payrollTotals.grossPay },
                { label: 'Payroll — Employer Taxes (from Payroll Journal)', amount: payrollTotals.employerTaxesTotal },
                { label: 'Payroll — Employer Benefits (from Payroll Journal)', amount: payrollTotals.employerBenefits },
            ];
            payrollItems.forEach(({ label, amount }) => {
                if (amount > 0) {
                    expenseMap[label] = (expenseMap[label] || 0) + amount;
                    if (!allExpenseTxMap[label]) allExpenseTxMap[label] = [];
                    allExpenseTxMap[label].push({
                        id: `payroll-${label}`,
                        description: label,
                        amount: -amount,
                        date: `${taxYear}-12-31`,
                        tag: { tag: label, type: 'Business' },
                        type: 'expense',
                        source: 'payroll-journal'
                    });
                }
            });
        }

        const incomeBySource = Object.entries(incomeMap)
            .filter(([, amount]) => amount > 0)
            .map(([source, amount]) => ({ source, amount }))
            .sort((a, b) => b.amount - a.amount);
        const expenseByCategory = Object.entries(expenseMap)
            .filter(([, amount]) => amount > 0)
            .map(([category, amount]) => ({ category, amount }))
            .sort((a, b) => b.amount - a.amount);
        const totalIncome = incomeBySource.reduce((s, r) => s + r.amount, 0);
        const totalExpenses = expenseByCategory.reduce((s, r) => s + r.amount, 0);
        return { incomeBySource, expenseByCategory, totalIncome, totalExpenses, netProfit: totalIncome - totalExpenses, allIncomeTxMap, allExpenseTxMap };
    }, [transactions, personalBizTx, payrollTotals, taxYear]);

    const w2Totals = useMemo(() => {
        let wages = 0, fedWithheld = 0, stateWithheld = 0;
        w2Entries.forEach(e => {
            wages += parseFloat(e.wages) || 0;
            fedWithheld += parseFloat(e.fedWithheld) || 0;
            stateWithheld += parseFloat(e.stateWithheld) || 0;
        });
        return { wages, fedWithheld, stateWithheld };
    }, [w2Entries]);

    const depStats = useMemo(() => {
        const childrenUnder17 = dependents.filter(d => { const a = parseInt(d.age); return !isNaN(a) && a < 17; }).length;
        const childrenUnder6 = dependents.filter(d => { const a = parseInt(d.age); return !isNaN(a) && a < 6; }).length;
        return { childrenUnder17, childrenUnder6, total: dependents.length };
    }, [dependents]);

    const taxEstimate = useMemo(() => {
        const totalGrossIncome = netProfit + w2Totals.wages;
        const hasIncome = totalGrossIncome > 0 || netProfit > 0;
        const empty = { seBase: 0, seTax: 0, ssSE: 0, medicareSE: 0, additionalMedicare: 0, seDeduction: 0, agi: 0, w2Wages: 0, federalStd: 0, federalTaxableIncome: 0, federalTax: 0, federalBrackets: [], fedWithheld: 0, fedOwed: 0, ctcTotal: 0, ctcApplied: 0, ctcRefundable: 0, caAgi: 0, caStd: 0, caTaxableIncome: 0, caTax: 0, caMentalHealth: 0, caTotalTax: 0, caBrackets: [], caWithheld: 0, caOwed: 0, caYctc: 0, totalTax: 0, totalCredits: 0, totalWithheld: 0, totalOwed: 0, effectiveRate: 0, quarterlyPayment: 0 };
        if (!hasIncome) return empty;

        const fedConfig = FEDERAL_BRACKETS[filingStatus] || FEDERAL_BRACKETS.single;
        const caConfig = CA_BRACKETS[filingStatus] || CA_BRACKETS.single;

        // Self-employment tax (only on SE net profit)
        let seBase = 0, ssSE = 0, medicareSE = 0, additionalMedicare = 0, seTax = 0;
        if (netProfit > 0) {
            seBase = netProfit * 0.9235;
            ssSE = Math.min(seBase, SS_WAGE_BASE) * 0.124;
            medicareSE = seBase * 0.029;
            const medThreshold = filingStatus === 'mfj' ? 250000 : 200000;
            additionalMedicare = seBase > medThreshold ? (seBase - medThreshold) * 0.009 : 0;
            seTax = ssSE + medicareSE + additionalMedicare;
        }
        const seDeduction = seTax / 2;

        // Federal income tax on combined household income
        const agi = Math.max(0, netProfit) + w2Totals.wages - seDeduction;
        const federalTaxableIncome = Math.max(0, agi - fedConfig.std);
        const { tax: federalTaxBeforeCredits, details: federalBrackets } = calcBracketTax(federalTaxableIncome, fedConfig.rates);

        // Child Tax Credit: $2,000 per child under 17
        // Phase-out: $50 per $1,000 over $400K (MFJ) / $200K (others)
        const ctcPerChild = 2000;
        const ctcRefundablePerChild = 1700;
        const ctcPhaseOutThreshold = filingStatus === 'mfj' ? 400000 : 200000;
        let ctcTotal = depStats.childrenUnder17 * ctcPerChild;
        if (agi > ctcPhaseOutThreshold) {
            const reduction = Math.ceil((agi - ctcPhaseOutThreshold) / 1000) * 50;
            ctcTotal = Math.max(0, ctcTotal - reduction);
        }
        const ctcApplied = Math.min(ctcTotal, federalTaxBeforeCredits);
        const ctcRefundable = Math.min(depStats.childrenUnder17 * ctcRefundablePerChild, ctcTotal - ctcApplied);

        const federalTax = Math.max(0, federalTaxBeforeCredits - ctcApplied);
        const fedOwed = Math.max(0, federalTax - w2Totals.fedWithheld) - ctcRefundable;

        // California state tax
        const caAgi = Math.max(0, netProfit) + w2Totals.wages - seDeduction;
        const caTaxableIncome = Math.max(0, caAgi - caConfig.std);
        const { tax: caTaxBeforeCredits, details: caBrackets } = calcBracketTax(caTaxableIncome, caConfig.rates);
        const caMentalHealth = caTaxableIncome > CA_MENTAL_HEALTH_THRESHOLD ? (caTaxableIncome - CA_MENTAL_HEALTH_THRESHOLD) * 0.01 : 0;

        // CA Young Child Tax Credit: up to $1,117 per child under 6 (income limit: CA AGI ≤ $30,931)
        const caYctc = caAgi <= 30931 ? depStats.childrenUnder6 * 1117 : 0;

        const caTotalTax = Math.max(0, caTaxBeforeCredits + caMentalHealth - caYctc);
        const caOwed = Math.max(0, caTotalTax - w2Totals.stateWithheld);

        const totalCredits = ctcApplied + ctcRefundable + caYctc;
        const totalTax = federalTax + seTax + caTotalTax;
        const totalWithheld = w2Totals.fedWithheld + w2Totals.stateWithheld;
        const totalOwed = Math.max(0, totalTax - totalWithheld - ctcRefundable);
        const totalAllIncome = Math.max(0, netProfit) + w2Totals.wages;
        const effectiveRate = totalAllIncome > 0 ? (totalTax / totalAllIncome) * 100 : 0;
        const quarterlyPayment = totalOwed / 4;

        return {
            seBase, seTax, ssSE, medicareSE, additionalMedicare, seDeduction,
            agi, w2Wages: w2Totals.wages, federalStd: fedConfig.std, federalTaxableIncome,
            federalTaxBeforeCredits, federalTax, federalBrackets, fedWithheld: w2Totals.fedWithheld, fedOwed,
            ctcTotal, ctcApplied, ctcRefundable,
            caAgi, caStd: caConfig.std, caTaxableIncome, caTax: caTaxBeforeCredits, caMentalHealth, caTotalTax, caBrackets, caWithheld: w2Totals.stateWithheld, caOwed, caYctc,
            totalTax, totalCredits, totalWithheld, totalOwed, effectiveRate, quarterlyPayment
        };
    }, [netProfit, filingStatus, w2Totals, depStats]);

    const fmt = (n) => Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const [exporting, setExporting] = useState(false);

    const handleExportExcel = async () => {
        setExporting(true);
        try {
            const incomeTxMap = {};
            incomeBySource.forEach(r => {
                const txs = (allIncomeTxMap[r.source] || []).map(tx => ({
                    date: tx.date ? new Date(tx.date).toLocaleDateString() : '',
                    description: tx.description || '',
                    amount: Math.abs(tx.amount || 0),
                }));
                incomeTxMap[r.source] = txs;
            });

            const expenseTxMap = {};
            expenseByCategory.forEach(r => {
                const txs = (allExpenseTxMap[r.category] || []).map(tx => ({
                    date: tx.date ? new Date(tx.date).toLocaleDateString() : '',
                    description: tx.description || '',
                    vendor: extractVendor(tx.description) || '',
                    amount: tx.amount || 0,
                }));
                expenseTxMap[r.category] = txs;
            });

            const payload = {
                taxYear,
                filingStatus: FILING_STATUS_LABELS[filingStatus] || filingStatus,
                income: incomeBySource.map(r => ({ source: r.source, amount: r.amount })),
                incomeTxMap,
                expenses: expenseByCategory.map(r => ({ category: r.category, amount: r.amount })),
                expenseTxMap,
                w2Entries: w2Entries.map(e => ({
                    label: e.label,
                    wages: parseFloat(e.wages) || 0,
                    fedWithheld: parseFloat(e.fedWithheld) || 0,
                    stateWithheld: parseFloat(e.stateWithheld) || 0,
                })),
                taxEstimate: {
                    seTax: taxEstimate.seTax,
                    federalTaxBeforeCredits: taxEstimate.federalTaxBeforeCredits,
                    ctcTotal: taxEstimate.ctcTotal,
                    federalTax: taxEstimate.federalTax,
                    caTotalTax: taxEstimate.caTotalTax,
                    caYctc: taxEstimate.caYctc,
                    totalTax: taxEstimate.totalTax,
                    totalWithheld: taxEstimate.totalWithheld,
                    totalOwed: taxEstimate.totalOwed,
                    quarterlyPayment: taxEstimate.quarterlyPayment,
                },
            };

            const response = await api.post('/api/csv/export-excel', payload, {
                responseType: 'blob',
            });
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `TaxSummary_${taxYear}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Excel export failed', err);
        } finally {
            setExporting(false);
        }
    };

    const handleSaveTaxSummary = () => {
        setSaving(true);
        setSaveStatus(null);
        const payload = {
            taxYear,
            filingStatus,
            w2Entries: w2Entries.map(e => ({ label: e.label, wages: parseFloat(e.wages) || 0, fedWithheld: parseFloat(e.fedWithheld) || 0, stateWithheld: parseFloat(e.stateWithheld) || 0 })),
            dependents: dependents.map(d => ({ name: d.name, age: parseInt(d.age) || 0 })),
            totalIncome,
            totalExpenses,
            netProfit,
            w2TotalWages: w2Totals.wages,
            w2TotalFedWithheld: w2Totals.fedWithheld,
            w2TotalStateWithheld: w2Totals.stateWithheld,
            selfEmploymentTax: taxEstimate.seTax,
            federalTax: taxEstimate.federalTax,
            federalTaxBeforeCredits: taxEstimate.federalTaxBeforeCredits,
            childTaxCredit: taxEstimate.ctcTotal,
            caTotalTax: taxEstimate.caTotalTax,
            caYctc: taxEstimate.caYctc,
            totalTax: taxEstimate.totalTax,
            totalWithheld: taxEstimate.totalWithheld,
            totalOwed: taxEstimate.totalOwed,
            effectiveRate: parseFloat(taxEstimate.effectiveRate.toFixed(1)),
            quarterlyPayment: taxEstimate.quarterlyPayment,
        };
        api.post(`/api/csv/saveTaxSummary`, payload)
            .then(() => setSaveStatus('success'))
            .catch(err => {
                console.error('Error saving tax summary:', err);
                setSaveStatus('error');
            })
            .finally(() => setSaving(false));
    };

    useEffect(() => {
        api.get(`/api/csv/taxSummary`, { params: { taxYear } })
            .then(response => {
                const data = response.data;
                if (!data) return;
                if (data.filingStatus) setFilingStatus(data.filingStatus);
                if (data.w2Entries?.length > 0) {
                    setW2Entries(data.w2Entries.map((e, i) => ({ id: Date.now() + i, label: e.label || '', wages: String(e.wages || ''), fedWithheld: String(e.fedWithheld || ''), stateWithheld: String(e.stateWithheld || '') })));
                }
                if (data.dependents?.length > 0) {
                    setDependents(data.dependents.map((d, i) => ({ id: Date.now() + 100 + i, name: d.name || '', age: String(d.age || '') })));
                }
            })
            .catch(() => {});
    }, [taxYear]);

    const toggleIncomeRow = (source) => setExpandedIncome(prev => ({ ...prev, [source]: !prev[source] }));
    const toggleExpenseRow = (category) => setExpandedExpense(prev => ({ ...prev, [category]: !prev[category] }));

    const handleSaveTransactionType = useCallback((transactionId, type) => {
        if (type === 'Select') return;
        api.put(`/api/csv/transaction/${transactionId}`, { type })
            .then(() => {
                setTransactions(prev => prev.map(tx =>
                    tx.id === transactionId
                        ? { ...tx, classification: type, taxCategory: type === 'Personal' ? null : tx.taxCategory }
                        : tx
                ));
            })
            .catch(err => {
                console.error('Error saving transaction type:', err);
            });
    }, []);

    const handleSaveTaxCategory = useCallback((transactionId, taxCategory, source) => {
        if (source === 'personal-finance') {
            const realId = String(transactionId).replace('pf-', '');
            api.put(`/api/personal-finance/transactions/${realId}/tag`, {
                businessTag: 'Business', taxCategory
            }).then(() => {
                setPersonalBizTx(prev => prev.map(tx =>
                    tx.id === Number(realId) ? { ...tx, taxCategory } : tx
                ));
            }).catch(err => {
                console.error('Error saving PF tax category:', err);
            });
        } else {
            api.put(`/api/csv/transaction/${transactionId}`, { taxCategory })
                .then(() => {
                    setTransactions(prev => prev.map(tx =>
                        tx.id === transactionId ? { ...tx, taxCategory } : tx
                    ));
                })
                .catch(err => {
                    console.error('Error saving tax category:', err);
                });
        }
    }, []);

    const extractVendor = useCallback((desc) => {
        if (!desc) return 'Other';
        const d = desc.toUpperCase().trim();
        const cutIdx = [' DES:', ' ID:', ' INDN:', ' CCD', ' PPD', 'DEBIT CARD', 'PURCHASE ', 'ACH '].reduce((min, tok) => {
            const i = d.indexOf(tok);
            return i > 0 && i < min ? i : min;
        }, d.length);
        let vendor = d.substring(0, cutIdx).replace(/\d{2}\/\d{2}\s*/g, '').replace(/\s{2,}/g, ' ').trim();
        if (vendor.length > 40) vendor = vendor.substring(0, 40).trim();
        return vendor || 'Other';
    }, []);

    const TransactionDetailRows = ({ txList, color }) => {
        const [selected, setSelectedLocal] = useState(new Set());
        const [bulkCat, setBulkCat] = useState('');
        const [expandedVendors, setExpandedVendors] = useState({});

        if (!txList || txList.length === 0) return null;
        const sorted = [...txList].sort((a, b) => new Date(a.date) - new Date(b.date));
        const editableTxs = sorted.filter(tx => tx.source !== 'payroll-journal');
        const allSelected = editableTxs.length > 0 && editableTxs.every(tx => selected.has(tx.id));

        const vendorGroups = {};
        sorted.forEach(tx => {
            const vendor = extractVendor(tx.description);
            if (!vendorGroups[vendor]) vendorGroups[vendor] = [];
            vendorGroups[vendor].push(tx);
        });
        const vendorEntries = Object.entries(vendorGroups).sort((a, b) => {
            const totalA = a[1].reduce((s, t) => s + Math.abs(t.amount || 0), 0);
            const totalB = b[1].reduce((s, t) => s + Math.abs(t.amount || 0), 0);
            return totalB - totalA;
        });

        const toggleSel = (id) => setSelectedLocal(prev => {
            const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
        });
        const toggleAll = () => setSelectedLocal(prev =>
            editableTxs.every(tx => prev.has(tx.id)) ? new Set() : new Set(editableTxs.map(tx => tx.id))
        );
        const applyBulk = () => {
            if (!bulkCat) return;
            [...selected].forEach(id => {
                const tx = sorted.find(t => t.id === id);
                handleSaveTaxCategory(id, bulkCat, tx?.source);
            });
            setSelectedLocal(new Set());
            setBulkCat('');
        };
        const toggleVendor = (v) => setExpandedVendors(prev => ({ ...prev, [v]: !prev[v] }));

        const TxRow = ({ tx, i }) => {
            const txType = tx.classification || tx.tag?.type || 'Select';
            const isPersonal = txType.toLowerCase() === 'personal';
            const isBusiness = txType === 'Business';
            const isPayrollSource = tx.source === 'payroll-journal';
            const isPfSource = tx.source === 'personal-finance';
            const isTypeReadOnly = isPfSource || isPayrollSource;
            const isCatReadOnly = isPayrollSource;
            const catCode = tx.taxCategory || '';
            const isSel = selected.has(tx.id);
            return (
                <TableRow key={tx.id || i} sx={{ '&:last-child td': { borderBottom: 0 },
                    opacity: isPersonal ? 0.55 : 1,
                    backgroundColor: isSel ? 'rgba(102,126,234,0.08)' : isPersonal ? '#fafafa' : 'transparent' }}>
                    <TableCell padding="checkbox" sx={{ py: 0.5 }}>
                        {!isPayrollSource && (
                            <Checkbox size="small" checked={isSel} onChange={() => toggleSel(tx.id)} />
                        )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>{tx.date ? new Date(tx.date).toLocaleDateString() : '—'}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: '#555', maxWidth: 350 }}>
                        {tx.description || '—'}
                        {isPersonal && <Chip label="Personal" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem', backgroundColor: '#fff3e0', color: '#e65100' }} />}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontSize: '0.8rem', fontWeight: 600, color: isPersonal ? '#999' : color, textDecoration: isPersonal ? 'line-through' : 'none' }}>${fmt(Math.abs(tx.amount || 0))}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                        {!isTypeReadOnly ? (
                            <Select value={txType} onChange={(e) => handleSaveTransactionType(tx.id, e.target.value)} size="small"
                                sx={{ minWidth: 110, fontSize: '0.8rem', height: 28, '& .MuiSelect-select': { py: 0.25 },
                                    backgroundColor: txType === 'Personal' ? '#fff3e0' : txType === 'Business' ? '#e8f5e9' : '#fff' }}>
                                <MenuItem value="Select" disabled sx={{ fontSize: '0.8rem' }}>Select</MenuItem>
                                <MenuItem value="Personal" sx={{ fontSize: '0.8rem' }}>Personal</MenuItem>
                                <MenuItem value="Business" sx={{ fontSize: '0.8rem' }}>Business</MenuItem>
                            </Select>
                        ) : isPayrollSource ? (
                            <Chip label="Payroll Journal" size="small" sx={{ height: 22, fontSize: '0.75rem', backgroundColor: '#e8eaf6', color: '#3949ab' }} />
                        ) : (
                            <Chip label="Business" size="small" sx={{ height: 22, fontSize: '0.75rem', backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                        )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                        {isBusiness && !isCatReadOnly ? (
                            <Select value={catCode} onChange={(e) => handleSaveTaxCategory(tx.id, e.target.value, tx.source)} size="small" displayEmpty
                                sx={{ minWidth: 170, fontSize: '0.75rem', height: 28, '& .MuiSelect-select': { py: 0.25 },
                                    backgroundColor: catCode ? '#e8f5e9' : '#fff3e0' }}>
                                <MenuItem value="" sx={{ fontSize: '0.75rem', color: '#999' }}>Uncategorized</MenuItem>
                                {TAX_GROUPS.map(group => [
                                    <ListSubheader key={group} sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#667eea', lineHeight: '24px' }}>{group}</ListSubheader>,
                                    ...TAX_CATEGORIES.filter(c => c.group === group).map(c => (
                                        <MenuItem key={c.code} value={c.code} sx={{ fontSize: '0.75rem', pl: 3 }}>{c.code.toUpperCase()} – {c.label}</MenuItem>
                                    ))
                                ])}
                            </Select>
                        ) : catCode && TAX_CAT_LABELS[catCode] ? (
                            <Chip label={`${catCode.toUpperCase()} ${TAX_CAT_LABELS[catCode]}`} size="small"
                                sx={{ height: 22, fontSize: '0.7rem', backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                        ) : isPersonal && tx.personalCategory && PERSONAL_CAT_LABELS[tx.personalCategory] ? (
                            <Chip label={PERSONAL_CAT_LABELS[tx.personalCategory]} size="small"
                                sx={{ height: 22, fontSize: '0.7rem', backgroundColor: '#fff3e0', color: '#e65100' }} />
                        ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                    </TableCell>
                </TableRow>
            );
        };

        return (
            <Box>
                {selected.size > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, ml: 4, mb: 0.5,
                        borderRadius: 2, backgroundColor: '#e3f2fd', border: '1px solid #90caf9', flexWrap: 'wrap',
                        width: 'calc(100% - 32px)' }}>
                        <Chip label={`${selected.size} selected`} color="primary" size="small" sx={{ fontWeight: 700 }} />
                        <Select size="small" displayEmpty value={bulkCat}
                            onChange={(e) => setBulkCat(e.target.value)}
                            sx={{ minWidth: 200, fontSize: '0.8rem', height: 28, backgroundColor: '#fff' }}
                            renderValue={(v) => {
                                if (!v) return <em>Set Tax Category...</em>;
                                const c = TAX_CAT_MAP[v];
                                return c ? `${c.code.toUpperCase()} – ${c.label}` : v;
                            }}>
                            <MenuItem value=""><em>Select...</em></MenuItem>
                            {TAX_GROUPS.map(group => [
                                <ListSubheader key={group} sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#667eea', lineHeight: '24px' }}>{group}</ListSubheader>,
                                ...TAX_CATEGORIES.filter(c => c.group === group).map(c => (
                                    <MenuItem key={c.code} value={c.code} sx={{ fontSize: '0.8rem', pl: 3 }}>{c.code.toUpperCase()} – {c.label}</MenuItem>
                                ))
                            ])}
                        </Select>
                        <Button variant="contained" size="small" onClick={applyBulk} disabled={!bulkCat}
                            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', height: 28,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                            Apply
                        </Button>
                        <Button variant="text" size="small" onClick={() => setSelectedLocal(new Set())}
                            sx={{ textTransform: 'none', color: '#666', fontSize: '0.75rem' }}>
                            Clear
                        </Button>
                    </Box>
                )}
                <Table size="small" sx={{ ml: 4, mb: 1, width: 'calc(100% - 32px)', backgroundColor: '#fafbfc' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ py: 0.5 }}>
                                {editableTxs.length > 0 && (
                                    <Checkbox size="small" checked={allSelected}
                                        indeterminate={selected.size > 0 && !allSelected} onChange={toggleAll} />
                                )}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', py: 0.5 }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', py: 0.5 }}>Description</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', py: 0.5 }}>Amount</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', py: 0.5, width: 130 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', py: 0.5, width: 200 }}>Tax Category</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {vendorEntries.map(([vendor, txs]) => {
                            const vendorTotal = txs.reduce((s, t) => s + Math.abs(t.amount || 0), 0);
                            const isOpen = expandedVendors[vendor];
                            return (
                                <React.Fragment key={vendor}>
                                    <TableRow
                                        onClick={() => toggleVendor(vendor)}
                                        sx={{ cursor: 'pointer', backgroundColor: '#f0f4ff',
                                            '&:hover': { backgroundColor: '#e8ecf8' } }}>
                                        <TableCell sx={{ py: 0.5, pl: 1 }}>
                                            {isOpen ? <ExpandLess sx={{ fontSize: 16, color: '#667eea' }} /> : <ExpandMore sx={{ fontSize: 16, color: '#999' }} />}
                                        </TableCell>
                                        <TableCell colSpan={2} sx={{ py: 0.75, fontWeight: 700, fontSize: '0.8rem', color: '#333' }}>
                                            {vendor}
                                            <Chip label={txs.length} size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem', fontWeight: 700, backgroundColor: 'rgba(102,126,234,0.12)', color: '#667eea' }} />
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 0.75, fontWeight: 700, fontSize: '0.8rem', color }}>
                                            ${fmt(vendorTotal)}
                                        </TableCell>
                                        <TableCell colSpan={2} />
                                    </TableRow>
                                    {isOpen && txs.map((tx, i) => <TxRow key={tx.id || i} tx={tx} i={i} />)}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
        );
    };

    const StatCard = ({ title, value, icon, gradient }) => (
        <Grow in timeout={600}>
            <Card
                sx={{
                    background: gradient,
                    color: 'white',
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 24px rgba(0,0,0,0.15)' }
                }}
            >
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1, fontSize: '0.875rem' }}>{title}</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>${value}</Typography>
                        </Box>
                        <Box sx={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '12px', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
                    </Box>
                </CardContent>
                <Box sx={{ position: 'absolute', bottom: -20, right: -20, opacity: 0.1, fontSize: '120px' }}>{icon}</Box>
            </Card>
        </Grow>
    );

    const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);
    const hasAnyIncome = (fetched && !loading && (transactions.length > 0 || payrollEntries.length > 0)) || w2Entries.length > 0;
    const hasTaxableIncome = (netProfit > 0 || w2Totals.wages > 0);

    return (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', py: 4 }}>
            <Container maxWidth="xl">
                {/* Hero */}
                <Fade in timeout={800}>
                    <Paper elevation={0} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: 4, p: 4, mb: 4, position: 'relative', overflow: 'hidden' }}>
                        <Box position="relative" zIndex={1}>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Gavel sx={{ fontSize: 40, mr: 2 }} />
                                <Typography variant="h3" fontWeight={700}>Tax Summary</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="h6" sx={{ opacity: 0.9 }}>Annual income and expense breakdown for tax filing</Typography>
                                <Chip icon={<LocationOn sx={{ color: 'white !important' }} />} label="California" sx={{ ml: 1, backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, '& .MuiChip-icon': { color: 'white' } }} />
                            </Box>
                        </Box>
                        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', filter: 'blur(60px)' }} />
                    </Paper>
                </Fade>

                {/* Controls: Year + Filing Status + Export */}
                <Fade in timeout={1000}>
                    <Paper elevation={0} sx={{ borderRadius: 3, p: 3, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                <ReceiptLong sx={{ color: '#667eea' }} />
                                <Typography variant="h6" fontWeight={600}>Tax Year</Typography>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>Year</InputLabel>
                                    <Select value={taxYear} label="Year" onChange={(e) => setTaxYear(Number(e.target.value))} sx={{ fontWeight: 600 }}>
                                        {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                                <Typography variant="h6" fontWeight={600}>Filing Status</Typography>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel>Status</InputLabel>
                                    <Select value={filingStatus} label="Status" onChange={(e) => setFilingStatus(e.target.value)} sx={{ fontWeight: 600 }}>
                                        {Object.entries(FILING_STATUS_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box display="flex" gap={1.5} alignItems="center">
                                {saveStatus === 'success' && <Chip label="Saved!" size="small" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />}
                                {saveStatus === 'error' && <Chip label="Save failed" size="small" sx={{ backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600 }} />}
                                {hasAnyIncome && (
                                    <>
                                        <Button variant="outlined" startIcon={saving ? <CircularProgress size={16} /> : <Save />} onClick={handleSaveTaxSummary} disabled={saving}
                                            sx={{ borderColor: '#667eea', color: '#667eea', fontWeight: 600, textTransform: 'none', '&:hover': { borderColor: '#764ba2', backgroundColor: 'rgba(102,126,234,0.08)' } }}>
                                            {saving ? 'Saving...' : 'Save'}
                                        </Button>
                                        <Button variant="contained" startIcon={exporting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Download />} onClick={handleExportExcel} disabled={exporting}
                                            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 600, textTransform: 'none', px: 3, boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)', '&:hover': { background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)', boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)' } }}>
                                            {exporting ? 'Exporting...' : 'Export Excel'}
                                        </Button>
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Paper>
                </Fade>

                <TransactionSearch year={taxYear} />

                {/* W-2 Income Entry */}
                <Fade in timeout={1100}>
                    <Paper elevation={0} sx={{ borderRadius: 3, p: 4, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)' }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box sx={{ p: 1, borderRadius: 2, background: 'linear-gradient(135deg, rgba(102,126,234,0.15) 0%, rgba(118,75,162,0.15) 100%)' }}>
                                    <Person sx={{ color: '#667eea', fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight={700} color="text.primary">W-2 Income</Typography>
                                    <Typography variant="caption" color="text.secondary">Add W-2 forms from employment (e.g. spouse's W-2)</Typography>
                                </Box>
                            </Box>
                            <Box display="flex" gap={1}>
                                <Button variant="outlined" component="label" disabled={w2Uploading}
                                    startIcon={w2Uploading ? <CircularProgress size={16} /> : <CloudUpload />}
                                    sx={{ borderColor: '#11998e', color: '#11998e', fontWeight: 600, textTransform: 'none', '&:hover': { borderColor: '#0d8070', backgroundColor: 'rgba(17,153,142,0.08)' } }}>
                                    {w2Uploading ? 'Parsing...' : 'Upload W-2 PDF'}
                                    <input type="file" hidden accept=".pdf" onChange={handleW2Upload} />
                                </Button>
                                <Button variant="outlined" startIcon={<PersonAdd />} onClick={addW2}
                                    sx={{ borderColor: '#667eea', color: '#667eea', fontWeight: 600, textTransform: 'none', '&:hover': { borderColor: '#764ba2', backgroundColor: 'rgba(102,126,234,0.08)' } }}>
                                    Add Manual
                                </Button>
                            </Box>
                        </Box>
                        {w2Entries.length === 0 ? (
                            <Box py={3} textAlign="center" sx={{ border: '2px dashed #e0e0e0', borderRadius: 2, backgroundColor: '#fafafa' }}>
                                <Person sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                                <Typography color="text.secondary" fontWeight={500}>No W-2 forms added</Typography>
                                <Typography variant="body2" color="text.secondary">Click "Add W-2" to include employment income from you or your spouse</Typography>
                            </Box>
                        ) : (
                            <Box display="flex" flexDirection="column" gap={3}>
                                {w2Entries.map((entry, idx) => (
                                    <Paper key={entry.id} variant="outlined" sx={{ p: 3, borderRadius: 2, borderColor: '#e0e0e0' }}>
                                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                            <Typography variant="subtitle2" fontWeight={700} color="#667eea">W-2 #{idx + 1}</Typography>
                                            <IconButton size="small" onClick={() => removeW2(entry.id)} sx={{ color: '#ee0979' }}><Delete fontSize="small" /></IconButton>
                                        </Box>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={3}>
                                                <TextField fullWidth size="small" label="Name / Employer" placeholder="e.g. Husband - Acme Corp" value={entry.label} onChange={(e) => updateW2(entry.id, 'label', e.target.value)} />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField fullWidth size="small" label="Box 1: Wages" type="number" value={entry.wages} onChange={(e) => updateW2(entry.id, 'wages', e.target.value)}
                                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField fullWidth size="small" label="Box 2: Federal Tax Withheld" type="number" value={entry.fedWithheld} onChange={(e) => updateW2(entry.id, 'fedWithheld', e.target.value)}
                                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField fullWidth size="small" label="Box 17: CA State Tax Withheld" type="number" value={entry.stateWithheld} onChange={(e) => updateW2(entry.id, 'stateWithheld', e.target.value)}
                                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                ))}
                                {w2Entries.length > 0 && (
                                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                        <Box sx={{ flex: '1 1 180px', p: 2, borderRadius: 2, bgcolor: '#e8f5e9', border: '1px solid #a5d6a7' }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Total W-2 Wages</Typography>
                                            <Typography variant="h6" fontWeight={700} color="#2e7d32">${fmt(w2Totals.wages)}</Typography>
                                        </Box>
                                        <Box sx={{ flex: '1 1 180px', p: 2, borderRadius: 2, bgcolor: '#e3f2fd', border: '1px solid #90caf9' }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Federal Withheld</Typography>
                                            <Typography variant="h6" fontWeight={700} color="#1565c0">${fmt(w2Totals.fedWithheld)}</Typography>
                                        </Box>
                                        <Box sx={{ flex: '1 1 180px', p: 2, borderRadius: 2, bgcolor: '#fff3e0', border: '1px solid #ffcc80' }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>CA State Withheld</Typography>
                                            <Typography variant="h6" fontWeight={700} color="#e65100">${fmt(w2Totals.stateWithheld)}</Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Paper>
                </Fade>

                {/* Dependents */}
                <Fade in timeout={1150}>
                    <Paper elevation={0} sx={{ borderRadius: 3, p: 4, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)' }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box sx={{ p: 1, borderRadius: 2, background: 'linear-gradient(135deg, rgba(56,142,60,0.15) 0%, rgba(129,199,132,0.15) 100%)' }}>
                                    <ChildCare sx={{ color: '#388e3c', fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight={700} color="text.primary">Dependents</Typography>
                                    <Typography variant="caption" color="text.secondary">Children under 17 qualify for the Child Tax Credit ($2,000 each)</Typography>
                                </Box>
                            </Box>
                            <Button variant="outlined" startIcon={<PersonAdd />} onClick={addDependent}
                                sx={{ borderColor: '#388e3c', color: '#388e3c', fontWeight: 600, textTransform: 'none', '&:hover': { borderColor: '#2e7d32', backgroundColor: 'rgba(56,142,60,0.08)' } }}>
                                Add Dependent
                            </Button>
                        </Box>
                        {dependents.length === 0 ? (
                            <Box py={3} textAlign="center" sx={{ border: '2px dashed #e0e0e0', borderRadius: 2, backgroundColor: '#fafafa' }}>
                                <ChildCare sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                                <Typography color="text.secondary" fontWeight={500}>No dependents added</Typography>
                                <Typography variant="body2" color="text.secondary">Click "Add Dependent" to include children for tax credit calculations</Typography>
                            </Box>
                        ) : (
                            <Box display="flex" flexDirection="column" gap={2}>
                                {dependents.map((dep, idx) => (
                                    <Box key={dep.id} display="flex" alignItems="center" gap={2}>
                                        <Chip label={`#${idx + 1}`} size="small" sx={{ backgroundColor: '#e8f5e9', color: '#388e3c', fontWeight: 700 }} />
                                        <TextField size="small" label="Name" placeholder="e.g. Emma" value={dep.name} onChange={(e) => updateDependent(dep.id, 'name', e.target.value)} sx={{ flex: 2 }} />
                                        <TextField size="small" label="Age" type="number" value={dep.age} onChange={(e) => updateDependent(dep.id, 'age', e.target.value)} sx={{ width: 100 }}
                                            inputProps={{ min: 0, max: 25 }} />
                                        <Chip label={parseInt(dep.age) < 17 ? 'CTC eligible' : parseInt(dep.age) >= 17 ? 'Over 16' : ''} size="small"
                                            sx={{ minWidth: 100, backgroundColor: parseInt(dep.age) < 17 ? '#e8f5e9' : '#ffebee', color: parseInt(dep.age) < 17 ? '#2e7d32' : '#c62828', fontWeight: 600, display: dep.age ? 'flex' : 'none' }} />
                                        <IconButton size="small" onClick={() => removeDependent(dep.id)} sx={{ color: '#ee0979' }}><Delete fontSize="small" /></IconButton>
                                    </Box>
                                ))}
                                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#e8f5e9', border: '1px solid #a5d6a7', flex: '1 1 200px' }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>Federal Child Tax Credit</Typography>
                                        <Typography variant="h6" fontWeight={700} color="#2e7d32">
                                            ${fmt(depStats.childrenUnder17 * 2000)} <Typography component="span" variant="caption" color="text.secondary">({depStats.childrenUnder17} {depStats.childrenUnder17 === 1 ? 'child' : 'children'} &times; $2,000)</Typography>
                                        </Typography>
                                    </Box>
                                    {depStats.childrenUnder6 > 0 && (
                                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#fff3e0', border: '1px solid #ffcc80', flex: '1 1 200px' }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>CA Young Child Tax Credit</Typography>
                                            <Typography variant="h6" fontWeight={700} color="#e65100">
                                                Up to ${fmt(depStats.childrenUnder6 * 1117)} <Typography component="span" variant="caption" color="text.secondary">({depStats.childrenUnder6} under 6 &bull; income limits apply)</Typography>
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </Fade>

                {loading && (
                    <Box display="flex" justifyContent="center" py={10}>
                        <CircularProgress size={60} sx={{ color: '#667eea' }} />
                    </Box>
                )}

                {fetched && !loading && transactions.length === 0 && payrollEntries.length === 0 && w2Entries.length === 0 && (
                    <Fade in timeout={800}>
                        <Paper elevation={0} sx={{ borderRadius: 3, p: 6, textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                            <ReceiptLong sx={{ fontSize: 80, color: '#667eea', opacity: 0.5, mb: 2 }} />
                            <Typography variant="h5" color="text.secondary" gutterBottom>No Income Data Yet</Typography>
                            <Typography variant="body1" color="text.secondary">Upload bank statements on the Upload page or add W-2 forms above to get started.</Typography>
                        </Paper>
                    </Fade>
                )}

                {fetched && !loading && (transactions.length > 0 || payrollEntries.length > 0) && (
                    <>
                        <Alert severity="info" sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-message': { fontWeight: 500 } }}>
                            Showing <strong>business transactions only</strong>. Personal transactions are excluded.
                            {' '}Transactions tagged as <em>Payroll Transfer</em> or <em>Credit Card Payment</em> are excluded (non-expense).
                            {payrollEntries.length > 0 && <> Payroll expenses sourced from <strong>Payroll Journal</strong> ({payrollEntries.length} employees).</>}
                        </Alert>

                        {/* Stat Cards */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} md={4}>
                                <StatCard title="Total Business Income" value={fmt(totalIncome)} icon={<AccountBalanceWallet sx={{ fontSize: 40 }} />} gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)" />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <StatCard title="Total Business Expenses" value={fmt(totalExpenses)} icon={<Receipt sx={{ fontSize: 40 }} />} gradient="linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)" />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <StatCard title={netProfit >= 0 ? 'Net SE Profit' : 'Net SE Loss'} value={fmt(Math.abs(netProfit))}
                                    icon={netProfit >= 0 ? <TrendingUp sx={{ fontSize: 40 }} /> : <TrendingDown sx={{ fontSize: 40 }} />}
                                    gradient={netProfit >= 0 ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'} />
                            </Grid>
                        </Grid>

                        {/* Income Table */}
                        <Fade in timeout={1200}>
                            <Paper elevation={0} sx={{ borderRadius: 3, p: 4, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)' }}>
                                <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                                    <Box sx={{ p: 1, borderRadius: 2, background: 'linear-gradient(135deg, rgba(17,153,142,0.15) 0%, rgba(56,239,125,0.15) 100%)' }}>
                                        <AccountBalanceWallet sx={{ color: '#11998e', fontSize: 28 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700} color="text.primary">Income by Source</Typography>
                                        <Typography variant="caption" color="text.secondary">Business income grouped by source for {taxYear}</Typography>
                                    </Box>
                                </Box>
                                {incomeBySource.length > 0 ? (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', width: 40 }} />
                                                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Source</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Transactions</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Amount</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>% of Total</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {incomeBySource.map(row => (
                                                    <React.Fragment key={row.source}>
                                                        <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleIncomeRow(row.source)}>
                                                            <TableCell sx={{ py: 1, pr: 0 }}>
                                                                {expandedIncome[row.source] ? <ExpandLess sx={{ fontSize: 20, color: '#11998e' }} /> : <ExpandMore sx={{ fontSize: 20, color: '#999' }} />}
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 500 }}>{row.source}</TableCell>
                                                            <TableCell align="right"><Chip label={allIncomeTxMap[row.source]?.length || 0} size="small" sx={{ fontWeight: 600, fontSize: '0.75rem', height: 22 }} /></TableCell>
                                                            <TableCell align="right" sx={{ color: '#11998e', fontWeight: 600 }}>${fmt(row.amount)}</TableCell>
                                                            <TableCell align="right" sx={{ color: 'text.secondary' }}>{totalIncome > 0 ? ((row.amount / totalIncome) * 100).toFixed(1) : 0}%</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell colSpan={5} sx={{ py: 0, borderBottom: expandedIncome[row.source] ? undefined : 'none' }}>
                                                                <Collapse in={expandedIncome[row.source]} timeout="auto" unmountOnExit>
                                                                    <TransactionDetailRows txList={allIncomeTxMap[row.source]} color="#11998e" />
                                                                </Collapse>
                                                            </TableCell>
                                                        </TableRow>
                                                    </React.Fragment>
                                                ))}
                                                <TableRow sx={{ backgroundColor: '#e8f5e9' }}>
                                                    <TableCell />
                                                    <TableCell sx={{ fontWeight: 700 }}>Total Income</TableCell>
                                                    <TableCell />
                                                    <TableCell align="right" sx={{ fontWeight: 700, color: '#11998e', fontSize: '1.05rem' }}>${fmt(totalIncome)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>100%</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Box py={4} textAlign="center"><Typography color="text.secondary">No business income recorded for {taxYear}</Typography></Box>
                                )}
                            </Paper>
                        </Fade>

                        {/* Expense Table */}
                        <Fade in timeout={1400}>
                            <Paper elevation={0} sx={{ borderRadius: 3, p: 4, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)' }}>
                                <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                                    <Box sx={{ p: 1, borderRadius: 2, background: 'linear-gradient(135deg, rgba(238,9,121,0.15) 0%, rgba(255,106,0,0.15) 100%)' }}>
                                        <Receipt sx={{ color: '#ee0979', fontSize: 28 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700} color="text.primary">Expenses by Category</Typography>
                                        <Typography variant="caption" color="text.secondary">Business expenses grouped by category for {taxYear}</Typography>
                                    </Box>
                                </Box>
                                {expenseByCategory.length > 0 ? (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', width: 40 }} />
                                                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Category</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Transactions</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Amount</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>% of Total</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {expenseByCategory.map(row => (
                                                    <React.Fragment key={row.category}>
                                                        <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleExpenseRow(row.category)}>
                                                            <TableCell sx={{ py: 1, pr: 0 }}>
                                                                {expandedExpense[row.category] ? <ExpandLess sx={{ fontSize: 20, color: '#ee0979' }} /> : <ExpandMore sx={{ fontSize: 20, color: '#999' }} />}
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 500 }}>{row.category}</TableCell>
                                                            <TableCell align="right"><Chip label={allExpenseTxMap[row.category]?.length || 0} size="small" sx={{ fontWeight: 600, fontSize: '0.75rem', height: 22 }} /></TableCell>
                                                            <TableCell align="right" sx={{ color: '#ee0979', fontWeight: 600 }}>${fmt(row.amount)}</TableCell>
                                                            <TableCell align="right" sx={{ color: 'text.secondary' }}>{totalExpenses > 0 ? ((row.amount / totalExpenses) * 100).toFixed(1) : 0}%</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell colSpan={5} sx={{ py: 0, borderBottom: expandedExpense[row.category] ? undefined : 'none' }}>
                                                                <Collapse in={expandedExpense[row.category]} timeout="auto" unmountOnExit>
                                                                    <TransactionDetailRows txList={allExpenseTxMap[row.category]} color="#ee0979" />
                                                                </Collapse>
                                                            </TableCell>
                                                        </TableRow>
                                                    </React.Fragment>
                                                ))}
                                                <TableRow sx={{ backgroundColor: '#ffebee' }}>
                                                    <TableCell />
                                                    <TableCell sx={{ fontWeight: 700 }}>Total Expenses</TableCell>
                                                    <TableCell />
                                                    <TableCell align="right" sx={{ fontWeight: 700, color: '#ee0979', fontSize: '1.05rem' }}>${fmt(totalExpenses)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>100%</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Box py={4} textAlign="center"><Typography color="text.secondary">No business expenses recorded for {taxYear}</Typography></Box>
                                )}
                            </Paper>
                        </Fade>

                        {/* Net Summary */}
                        <Fade in timeout={1600}>
                            <Paper elevation={0} sx={{ borderRadius: 3, p: 4, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)' }}>
                                <Typography variant="h6" fontWeight={700} mb={3}>{taxYear} Combined Income Summary</Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Grid container spacing={2}>
                                    <Grid item xs={7}><Typography variant="body1" color="text.secondary">Net Self-Employment Profit</Typography></Grid>
                                    <Grid item xs={5}><Typography variant="body1" fontWeight={600} align="right" sx={{ color: netProfit >= 0 ? '#11998e' : '#ee0979' }}>{netProfit < 0 ? '(' : ''}${fmt(Math.abs(netProfit))}{netProfit < 0 ? ')' : ''}</Typography></Grid>
                                    {w2Totals.wages > 0 && (
                                        <>
                                            <Grid item xs={7}><Typography variant="body1" color="text.secondary">W-2 Wages</Typography></Grid>
                                            <Grid item xs={5}><Typography variant="body1" fontWeight={600} align="right" sx={{ color: '#11998e' }}>${fmt(w2Totals.wages)}</Typography></Grid>
                                        </>
                                    )}
                                    <Grid item xs={12}><Divider /></Grid>
                                    <Grid item xs={7}><Typography variant="h6" fontWeight={700}>Total Household Income</Typography></Grid>
                                    <Grid item xs={5}><Typography variant="h6" fontWeight={700} align="right" sx={{ color: '#11998e' }}>${fmt(Math.max(0, netProfit) + w2Totals.wages)}</Typography></Grid>
                                </Grid>
                            </Paper>
                        </Fade>
                    </>
                )}

                {/* Tax Liability (shown when there's any taxable income) */}
                {hasTaxableIncome && fetched && !loading && (
                    <>
                        {/* Self-Employment Tax */}
                        {netProfit > 0 && (
                            <Fade in timeout={1800}>
                                <Paper elevation={0} sx={{ borderRadius: 3, p: 4, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)' }}>
                                    <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                                        <Box sx={{ p: 1, borderRadius: 2, background: 'linear-gradient(135deg, rgba(118,75,162,0.15) 0%, rgba(102,126,234,0.15) 100%)' }}>
                                            <Calculate sx={{ color: '#764ba2', fontSize: 28 }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="h6" fontWeight={700}>Self-Employment Tax</Typography>
                                            <Typography variant="caption" color="text.secondary">Schedule SE &bull; 15.3% on 92.35% of net earnings</Typography>
                                        </Box>
                                    </Box>
                                    <Grid container spacing={1.5} sx={{ pl: 1 }}>
                                        <Grid item xs={7}><Typography variant="body2" color="text.secondary">Net profit &times; 92.35%</Typography></Grid>
                                        <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right">${fmt(taxEstimate.seBase)}</Typography></Grid>
                                        <Grid item xs={7}><Typography variant="body2" color="text.secondary">Social Security (12.4%, up to $176,100)</Typography></Grid>
                                        <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right">${fmt(taxEstimate.ssSE)}</Typography></Grid>
                                        <Grid item xs={7}><Typography variant="body2" color="text.secondary">Medicare (2.9%)</Typography></Grid>
                                        <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right">${fmt(taxEstimate.medicareSE)}</Typography></Grid>
                                        {taxEstimate.additionalMedicare > 0 && (
                                            <>
                                                <Grid item xs={7}><Typography variant="body2" color="text.secondary">Additional Medicare (0.9%)</Typography></Grid>
                                                <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right">${fmt(taxEstimate.additionalMedicare)}</Typography></Grid>
                                            </>
                                        )}
                                        <Grid item xs={12}><Divider /></Grid>
                                        <Grid item xs={7}><Typography variant="body1" fontWeight={700}>Total Self-Employment Tax</Typography></Grid>
                                        <Grid item xs={5}><Typography variant="body1" fontWeight={700} align="right" sx={{ color: '#764ba2' }}>${fmt(taxEstimate.seTax)}</Typography></Grid>
                                    </Grid>
                                </Paper>
                            </Fade>
                        )}

                        {/* Federal Income Tax */}
                        <Fade in timeout={1900}>
                            <Paper elevation={0} sx={{ borderRadius: 3, p: 4, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)' }}>
                                <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                                    <Box sx={{ p: 1, borderRadius: 2, background: 'linear-gradient(135deg, rgba(102,126,234,0.15) 0%, rgba(118,75,162,0.15) 100%)' }}>
                                        <Calculate sx={{ color: '#667eea', fontSize: 28 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>Federal Income Tax</Typography>
                                        <Typography variant="caption" color="text.secondary">{taxYear} brackets &bull; {FILING_STATUS_LABELS[filingStatus]}</Typography>
                                    </Box>
                                </Box>
                                <Grid container spacing={1.5} sx={{ mb: 2, pl: 1 }}>
                                    {netProfit > 0 && (
                                        <>
                                            <Grid item xs={7}><Typography variant="body2" color="text.secondary">Net SE profit</Typography></Grid>
                                            <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right">${fmt(netProfit)}</Typography></Grid>
                                        </>
                                    )}
                                    {w2Totals.wages > 0 && (
                                        <>
                                            <Grid item xs={7}><Typography variant="body2" color="text.secondary">W-2 wages</Typography></Grid>
                                            <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right">${fmt(w2Totals.wages)}</Typography></Grid>
                                        </>
                                    )}
                                    {netProfit > 0 && (
                                        <>
                                            <Grid item xs={7}><Typography variant="body2" color="text.secondary">Deductible half of SE tax</Typography></Grid>
                                            <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right" sx={{ color: '#ee0979' }}>(${fmt(taxEstimate.seDeduction)})</Typography></Grid>
                                        </>
                                    )}
                                    <Grid item xs={7}><Typography variant="body2" color="text.secondary">Adjusted Gross Income (AGI)</Typography></Grid>
                                    <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right">${fmt(taxEstimate.agi)}</Typography></Grid>
                                    <Grid item xs={7}><Typography variant="body2" color="text.secondary">Standard deduction</Typography></Grid>
                                    <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right" sx={{ color: '#ee0979' }}>(${fmt(taxEstimate.federalStd)})</Typography></Grid>
                                    <Grid item xs={12}><Divider /></Grid>
                                    <Grid item xs={7}><Typography variant="body1" fontWeight={600}>Taxable Income</Typography></Grid>
                                    <Grid item xs={5}><Typography variant="body1" fontWeight={700} align="right">${fmt(taxEstimate.federalTaxableIncome)}</Typography></Grid>
                                </Grid>
                                {taxEstimate.federalBrackets.length > 0 && (
                                    <TableContainer sx={{ my: 2 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Bracket</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Taxable Amount</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Tax</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {taxEstimate.federalBrackets.map(b => (
                                                    <TableRow key={b.rate} hover>
                                                        <TableCell sx={{ fontWeight: 500 }}>{b.rate}%</TableCell>
                                                        <TableCell align="right">${fmt(b.taxable)}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 600, color: '#667eea' }}>${fmt(b.tax)}</TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow sx={{ backgroundColor: '#e8eaf6' }}>
                                                            <TableCell sx={{ fontWeight: 700 }}>Federal Tax (before credits)</TableCell>
                                                            <TableCell />
                                                            <TableCell align="right" sx={{ fontWeight: 700, color: '#667eea', fontSize: '1.05rem' }}>${fmt(taxEstimate.federalTaxBeforeCredits)}</TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                {taxEstimate.ctcTotal > 0 && (
                                    <Grid container spacing={1.5} sx={{ mt: 1, pl: 1 }}>
                                        <Grid item xs={7}><Typography variant="body2" color="text.secondary">Child Tax Credit ({depStats.childrenUnder17} {depStats.childrenUnder17 === 1 ? 'child' : 'children'} &times; $2,000)</Typography></Grid>
                                        <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right" sx={{ color: '#388e3c' }}>(${fmt(taxEstimate.ctcApplied)})</Typography></Grid>
                                        {taxEstimate.ctcRefundable > 0 && (
                                            <>
                                                <Grid item xs={7}><Typography variant="body2" color="text.secondary">Refundable portion (Additional CTC)</Typography></Grid>
                                                <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right" sx={{ color: '#388e3c' }}>${fmt(taxEstimate.ctcRefundable)}</Typography></Grid>
                                            </>
                                        )}
                                        <Grid item xs={12}><Divider /></Grid>
                                        <Grid item xs={7}><Typography variant="body1" fontWeight={700}>Federal Tax (after credits)</Typography></Grid>
                                        <Grid item xs={5}><Typography variant="body1" fontWeight={700} align="right" sx={{ color: '#667eea' }}>${fmt(taxEstimate.federalTax)}</Typography></Grid>
                                    </Grid>
                                )}
                                {w2Totals.fedWithheld > 0 && (
                                    <Grid container spacing={1.5} sx={{ mt: 1, pl: 1 }}>
                                        <Grid item xs={7}><Typography variant="body2" color="text.secondary">Already withheld (W-2)</Typography></Grid>
                                        <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right" sx={{ color: '#11998e' }}>(${fmt(w2Totals.fedWithheld)})</Typography></Grid>
                                        <Grid item xs={12}><Divider /></Grid>
                                        <Grid item xs={7}><Typography variant="body1" fontWeight={700}>Federal Tax Still Owed</Typography></Grid>
                                        <Grid item xs={5}><Typography variant="body1" fontWeight={700} align="right" sx={{ color: '#ee0979' }}>${fmt(taxEstimate.fedOwed)}</Typography></Grid>
                                    </Grid>
                                )}
                            </Paper>
                        </Fade>

                        {/* California State Tax */}
                        <Fade in timeout={2000}>
                            <Paper elevation={0} sx={{ borderRadius: 3, p: 4, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)' }}>
                                <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                                    <Box sx={{ p: 1, borderRadius: 2, background: 'linear-gradient(135deg, rgba(255,152,0,0.15) 0%, rgba(255,87,34,0.15) 100%)' }}>
                                        <LocationOn sx={{ color: '#e65100', fontSize: 28 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>California State Tax (FTB)</Typography>
                                        <Typography variant="caption" color="text.secondary">{taxYear} CA brackets &bull; {FILING_STATUS_LABELS[filingStatus]}</Typography>
                                    </Box>
                                </Box>
                                <Grid container spacing={1.5} sx={{ mb: 2, pl: 1 }}>
                                    <Grid item xs={7}><Typography variant="body2" color="text.secondary">California AGI</Typography></Grid>
                                    <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right">${fmt(taxEstimate.caAgi)}</Typography></Grid>
                                    <Grid item xs={7}><Typography variant="body2" color="text.secondary">CA standard deduction</Typography></Grid>
                                    <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right" sx={{ color: '#ee0979' }}>(${fmt(taxEstimate.caStd)})</Typography></Grid>
                                    <Grid item xs={12}><Divider /></Grid>
                                    <Grid item xs={7}><Typography variant="body1" fontWeight={600}>CA Taxable Income</Typography></Grid>
                                    <Grid item xs={5}><Typography variant="body1" fontWeight={700} align="right">${fmt(taxEstimate.caTaxableIncome)}</Typography></Grid>
                                </Grid>
                                {taxEstimate.caBrackets.length > 0 && (
                                    <TableContainer sx={{ my: 2 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Bracket</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Taxable Amount</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Tax</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {taxEstimate.caBrackets.map(b => (
                                                    <TableRow key={b.rate} hover>
                                                        <TableCell sx={{ fontWeight: 500 }}>{b.rate}%</TableCell>
                                                        <TableCell align="right">${fmt(b.taxable)}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 600, color: '#e65100' }}>${fmt(b.tax)}</TableCell>
                                                    </TableRow>
                                                ))}
                                                {taxEstimate.caMentalHealth > 0 && (
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 500 }}>1% Mental Health Surcharge</TableCell>
                                                        <TableCell align="right">${fmt(taxEstimate.caTaxableIncome - CA_MENTAL_HEALTH_THRESHOLD)}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 600, color: '#e65100' }}>${fmt(taxEstimate.caMentalHealth)}</TableCell>
                                                    </TableRow>
                                                )}
                                                <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                                                    <TableCell sx={{ fontWeight: 700 }}>CA State Tax</TableCell>
                                                    <TableCell />
                                                    <TableCell align="right" sx={{ fontWeight: 700, color: '#e65100', fontSize: '1.05rem' }}>${fmt(taxEstimate.caTotalTax)}</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                                {(taxEstimate.caYctc > 0 || w2Totals.stateWithheld > 0) && (
                                    <Grid container spacing={1.5} sx={{ mt: 1, pl: 1 }}>
                                        {taxEstimate.caYctc > 0 && (
                                            <>
                                                <Grid item xs={7}><Typography variant="body2" color="text.secondary">Young Child Tax Credit ({depStats.childrenUnder6} under 6)</Typography></Grid>
                                                <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right" sx={{ color: '#388e3c' }}>(${fmt(taxEstimate.caYctc)})</Typography></Grid>
                                            </>
                                        )}
                                        {w2Totals.stateWithheld > 0 && (
                                            <>
                                                <Grid item xs={7}><Typography variant="body2" color="text.secondary">Already withheld (W-2)</Typography></Grid>
                                                <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right" sx={{ color: '#11998e' }}>(${fmt(w2Totals.stateWithheld)})</Typography></Grid>
                                            </>
                                        )}
                                        <Grid item xs={12}><Divider /></Grid>
                                        <Grid item xs={7}><Typography variant="body1" fontWeight={700}>CA Tax Still Owed</Typography></Grid>
                                        <Grid item xs={5}><Typography variant="body1" fontWeight={700} align="right" sx={{ color: '#ee0979' }}>${fmt(taxEstimate.caOwed)}</Typography></Grid>
                                    </Grid>
                                )}
                            </Paper>
                        </Fade>

                        {/* Grand Total */}
                        <Fade in timeout={2100}>
                            <Paper elevation={0} sx={{ borderRadius: 3, p: 4, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '2px solid rgba(102,126,234,0.3)', background: 'linear-gradient(135deg, rgba(102,126,234,0.03) 0%, rgba(118,75,162,0.03) 100%)' }}>
                                <Typography variant="h6" fontWeight={700} mb={3}>Total Estimated Tax Liability</Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Grid container spacing={2}>
                                    {netProfit > 0 && (
                                        <>
                                            <Grid item xs={7}><Typography variant="body1" color="text.secondary">Self-Employment Tax</Typography></Grid>
                                            <Grid item xs={5}><Typography variant="body1" fontWeight={600} align="right">${fmt(taxEstimate.seTax)}</Typography></Grid>
                                        </>
                                    )}
                                    <Grid item xs={7}><Typography variant="body1" color="text.secondary">Federal Income Tax (after CTC)</Typography></Grid>
                                    <Grid item xs={5}><Typography variant="body1" fontWeight={600} align="right">${fmt(taxEstimate.federalTax)}</Typography></Grid>
                                    <Grid item xs={7}><Typography variant="body1" color="text.secondary">California State Tax</Typography></Grid>
                                    <Grid item xs={5}><Typography variant="body1" fontWeight={600} align="right">${fmt(taxEstimate.caTotalTax)}</Typography></Grid>
                                    <Grid item xs={12}><Divider /></Grid>
                                    <Grid item xs={7}><Typography variant="h6" fontWeight={700}>Total Tax</Typography></Grid>
                                    <Grid item xs={5}><Typography variant="h6" fontWeight={700} align="right">${fmt(taxEstimate.totalTax)}</Typography></Grid>
                                    {taxEstimate.totalWithheld > 0 && (
                                        <>
                                            <Grid item xs={7}><Typography variant="body1" sx={{ color: '#11998e' }}>Already Withheld (W-2)</Typography></Grid>
                                            <Grid item xs={5}><Typography variant="body1" fontWeight={600} align="right" sx={{ color: '#11998e' }}>(${fmt(taxEstimate.totalWithheld)})</Typography></Grid>
                                        </>
                                    )}
                                    {taxEstimate.ctcRefundable > 0 && (
                                        <>
                                            <Grid item xs={7}><Typography variant="body1" sx={{ color: '#388e3c' }}>Refundable Child Tax Credit</Typography></Grid>
                                            <Grid item xs={5}><Typography variant="body1" fontWeight={600} align="right" sx={{ color: '#388e3c' }}>(${fmt(taxEstimate.ctcRefundable)})</Typography></Grid>
                                        </>
                                    )}
                                    {(taxEstimate.totalWithheld > 0 || taxEstimate.ctcRefundable > 0) && (
                                        <Grid item xs={12}><Divider /></Grid>
                                    )}
                                    <Grid item xs={7}><Typography variant="h5" fontWeight={700} sx={{ color: '#ee0979' }}>Estimated Amount Owed</Typography></Grid>
                                    <Grid item xs={5}><Typography variant="h5" fontWeight={700} align="right" sx={{ color: '#ee0979' }}>${fmt(taxEstimate.totalOwed)}</Typography></Grid>
                                    <Grid item xs={7}><Typography variant="body2" color="text.secondary">Effective tax rate</Typography></Grid>
                                    <Grid item xs={5}><Typography variant="body2" fontWeight={600} align="right" color="text.secondary">{taxEstimate.effectiveRate.toFixed(1)}%</Typography></Grid>
                                </Grid>
                            </Paper>
                        </Fade>

                        {/* Quarterly Estimated Payments */}
                        {taxEstimate.totalOwed > 0 && (
                            <Fade in timeout={2200}>
                                <Paper elevation={0} sx={{ borderRadius: 3, p: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)' }}>
                                    <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                                        <Box sx={{ p: 1, borderRadius: 2, background: 'linear-gradient(135deg, rgba(238,9,121,0.15) 0%, rgba(255,106,0,0.15) 100%)' }}>
                                            <CalendarMonth sx={{ color: '#ee0979', fontSize: 28 }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="h6" fontWeight={700}>Quarterly Estimated Payments</Typography>
                                            <Typography variant="caption" color="text.secondary">IRS Form 1040-ES + CA Form 540-ES</Typography>
                                        </Box>
                                    </Box>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Quarter</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Due Date</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Amount</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {[
                                                    { q: 'Q1 (Jan–Mar)', due: `Apr 15, ${taxYear}` },
                                                    { q: 'Q2 (Apr–May)', due: `Jun 15, ${taxYear}` },
                                                    { q: 'Q3 (Jun–Aug)', due: `Sep 15, ${taxYear}` },
                                                    { q: 'Q4 (Sep–Dec)', due: `Jan 15, ${taxYear + 1}` },
                                                ].map(row => (
                                                    <TableRow key={row.q} hover>
                                                        <TableCell sx={{ fontWeight: 500 }}>{row.q}</TableCell>
                                                        <TableCell>{row.due}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 600, color: '#ee0979' }}>${fmt(taxEstimate.quarterlyPayment)}</TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow sx={{ backgroundColor: '#ffebee' }}>
                                                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                                    <TableCell />
                                                    <TableCell align="right" sx={{ fontWeight: 700, color: '#ee0979', fontSize: '1.05rem' }}>${fmt(taxEstimate.totalOwed)}</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Alert severity="warning" sx={{ mt: 3, borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.85rem' } }}>
                                        This is an estimate using {taxYear} federal and California tax brackets with standard deductions. It does not account for credits (EITC, child tax credit, etc.), itemized deductions, AMT, or other adjustments. Consult a tax professional for accurate filing.
                                    </Alert>
                                </Paper>
                            </Fade>
                        )}
                    </>
                )}
            </Container>
        </Box>
    );
}

export default TaxSummary;
